import {
  Project,
  SyntaxKind,
  Node,
  ObjectLiteralExpression,
  PropertyAssignment,
  SourceFile,
} from "ts-morph";
import path from "path";

export interface EnvVar {
  key: string;
  type: string;
  comment?: string;
  node: PropertyAssignment;
}

export interface ParsedSchema {
  client: Record<string, EnvVar>;
  server: Record<string, EnvVar>;
  sourceFile: SourceFile;
}

export interface EnvVarInput {
  key: string;
  type: string;
  comment?: string;
}

export interface MergeSchemaResult {
  updated: boolean;
  added: string[];
  updatedVars: string[];
  removed: string[];
}

/**
 * Parse the env schema file and extract client/server variables, types, and comments.
 */
export function parseEnvSchema(schemaPath: string): ParsedSchema {
  const project = new Project({
    tsConfigFilePath: path.resolve("tsconfig.json"),
    skipAddingFilesFromTsConfig: true,
  });
  const sourceFile = project.addSourceFileAtPath(schemaPath);

  // Find the createEnv call
  const createEnvCall = sourceFile.getFirstDescendant((node) => {
    return (
      Node.isCallExpression(node) &&
      node.getExpression().getText().includes("createEnv")
    );
  });
  if (!createEnvCall)
    throw new Error("Could not find createEnv call in schema");

  // Cast to CallExpression
  const callExpr = createEnvCall.asKindOrThrow(SyntaxKind.CallExpression);
  const arg = callExpr.getArguments()[0];
  if (!Node.isObjectLiteralExpression(arg))
    throw new Error("createEnv arg is not an object");
  const obj = arg as ObjectLiteralExpression;

  function extractVars(section: "client" | "server") {
    const prop = obj.getProperty(section);
    if (!prop || !Node.isPropertyAssignment(prop)) return {};
    const value = prop.getInitializerIfKind(SyntaxKind.ObjectLiteralExpression);
    if (!value) return {};
    const vars: Record<string, EnvVar> = {};
    for (const p of value.getProperties()) {
      if (!Node.isPropertyAssignment(p)) continue;
      const key = p.getName().replace(/^"|"$/g, "");
      const typeNode = p.getInitializer();
      const type = typeNode ? typeNode.getText() : "z.string()";
      // Find preceding comment
      const commentRanges = p.getLeadingCommentRanges();
      const comment =
        commentRanges.length > 0 ? commentRanges[0].getText() : undefined;
      vars[key] = { key, type, comment, node: p };
    }
    return vars;
  }

  return {
    client: extractVars("client"),
    server: extractVars("server"),
    sourceFile,
  };
}

/**
 * Merge .env variables into the parsed schema, updating, adding, or flagging variables as needed.
 * Modifies the AST in-place. Returns a summary of changes.
 */
export function mergeSchema(
  parsed: ParsedSchema,
  envVars: {
    client: Record<string, EnvVarInput>;
    server: Record<string, EnvVarInput>;
  },
  options?: { flagRemoved?: boolean }
): MergeSchemaResult {
  const { client, server, sourceFile } = parsed;
  const added: string[] = [];
  const updatedVars: string[] = [];
  const removed: string[] = [];
  const flagRemoved = options?.flagRemoved ?? true;

  function mergeSection(
    section: "client" | "server",
    schemaVars: Record<string, EnvVar>,
    envVars: Record<string, EnvVarInput>
  ) {
    // Add or update variables
    for (const [key, envVar] of Object.entries(envVars)) {
      if (schemaVars[key]) {
        // Update type if changed
        if (schemaVars[key].type !== envVar.type) {
          schemaVars[key].node.setInitializer(envVar.type);
          updatedVars.push(key);
        }
        // Optionally update comment
        if (envVar.comment && schemaVars[key].comment !== envVar.comment) {
          schemaVars[key].node.replaceWithText(
            `${envVar.comment}\n${schemaVars[key].node.getText()}`
          );
        }
      } else {
        // Add new variable
        const sectionObj = getSectionObject(section);
        sectionObj.addPropertyAssignment({
          name: /^[A-Za-z_][A-Za-z0-9_]*$/.test(key)
            ? key
            : JSON.stringify(key),
          initializer: envVar.type,
          leadingTrivia: envVar.comment ? envVar.comment + "\n" : undefined,
        });
        added.push(key);
      }
    }
    // Flag removed variables
    for (const key of Object.keys(schemaVars)) {
      if (!envVars[key] && flagRemoved) {
        // Comment out the line (simple approach: prepend //)
        const node = schemaVars[key].node;
        if (!node.getText().trim().startsWith("//")) {
          node.replaceWithText("// " + node.getText());
        }
        removed.push(key);
      }
    }
  }

  function getSectionObject(section: "client" | "server") {
    const createEnvCall = sourceFile.getFirstDescendant((node) => {
      return (
        Node.isCallExpression(node) &&
        node.getExpression().getText().includes("createEnv")
      );
    });
    if (!createEnvCall) throw new Error("Could not find createEnv call");
    const callExpr = createEnvCall.asKindOrThrow(SyntaxKind.CallExpression);
    const arg = callExpr.getArguments()[0];
    if (!Node.isObjectLiteralExpression(arg))
      throw new Error("createEnv arg is not an object");
    const obj = arg as ObjectLiteralExpression;
    const prop = obj.getProperty(section);
    if (!prop || !Node.isPropertyAssignment(prop))
      throw new Error(`No ${section} property in schema`);
    const value = prop.getInitializerIfKindOrThrow(
      SyntaxKind.ObjectLiteralExpression
    );
    return value;
  }

  mergeSection("client", client, envVars.client);
  mergeSection("server", server, envVars.server);

  return {
    updated: added.length > 0 || updatedVars.length > 0 || removed.length > 0,
    added,
    updatedVars,
    removed,
  };
}

// TODO: Add functions for merging/updating and regenerating the schema
